/**
 * CRO-WEB-173 Experiment Hook
 *
 * Comprehensive hook that encapsulates all experiment logic for CRO-WEB-173.
 * Handles both welcome step content and image display behavior.
 * This keeps components clean and experiment-agnostic.
 */

import { logger } from "@/src/utils/logger";
import { useCroWeb173Variant } from "./getCroWeb173Variant";

export interface CroWeb173ExperimentConfig {
  // Welcome step configuration
  showFeatureList: boolean;
  showTestimonial: boolean;

  // Image configuration
  hideHeroOnMobile: boolean;

  // General
  variant: string | null;
  isLoading: boolean;
}

/**
 * Hook that determines all experiment behavior for CRO-WEB-173
 */
export const useCroWeb173Experiment = (): CroWeb173ExperimentConfig => {
  const { variant, isInitialized } = useCroWeb173Variant();

  // Determine if we're still loading the variant
  // Always return false on server to prevent hydration mismatch
  // Only show loading on client after hydration
  const isLoading = false;

  // DEBUG: Log the variant value (not in production)
  logger.log(
    "🧪 CRO-WEB-173 Experiment variant:",
    variant,
    "isLoading:",
    isLoading
  );

  // Welcome step: Determine what to show based on variant
  const showFeatureList =
    !variant ||
    variant === "control" ||
    variant === "social-proof-features-no-hero";

  const showTestimonial =
    !variant ||
    variant === "control" ||
    variant === "social-proof-no-features" ||
    variant === "social-proof-no-features-no-hero" ||
    variant === "social-proof-features-no-hero";

  // Image: Check if we should hide hero on mobile for certain variants
  const hideHeroOnMobile =
    variant === "social-proof-no-features-no-hero" ||
    variant === "social-proof-features-no-hero";

  return {
    showFeatureList,
    showTestimonial,
    hideHeroOnMobile,
    variant,
    isLoading,
  };
};
